import BadgesComponent from '@components/common/Badges';
import Button from '@components/common/Button';
import React, { useEffect, useMemo, useState } from 'react';
import './index.css';
import PlusSvgIcon from '../../../../assets/svg_main/plus.svg';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setFamilyPlanningBackHandler, setFamilyPlanningDetails } from '@store/actions/beneficiaryAction';
import EyeOpen from '../../../../../src/assets/svg_main/eye-open.svg';
import EditIcon from '../../../../../src/assets/svg_main/edit_outline.svg';
import { getData } from '@services/CommonApiService';
import endpoints from '@config/endpoints';
import { useSelector } from 'react-redux';
import { RootState } from '@store/reducers';
import { errorLogger } from '@utils/applicationLogUtils';
import { COMMON_CONSTANTS } from '@constants/index';
import {  buttonNames } from '@constants/appConstants';

const FamilyPlanningServices: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const benfDetails = useSelector((state: RootState) => state.beneficiary.beneficiaryDetails);
    const familyPlanningDetails = useSelector((state: RootState) => state.beneficiary.familyPlanningDetails);
    const userRole = benfDetails?.user_role || localStorage.getItem('userTypeName');
    
    const latestFamilyPlanningCreateDate = useMemo(() => {
        const allDates = (familyPlanningDetails || [])
            .map((s: any) => s.create_date)
            .filter(Boolean);
        if (!allDates.length) return null;
        return allDates.reduce((max: any, d: any) =>
            dayjs(d).isAfter(dayjs(max)) ? d : max
        );
    }, [familyPlanningDetails]);

    const [isAddButtonDisable,setIsAddButtonDisable] = useState<boolean>(false);
    const [pregnancyHistory, setPregnancyHistory] = useState<any[]>([]);

    useEffect(() => {
        initialFetch();
    }, [])

    const initialFetch = async () => {
        try {
            const pregnancyHistoryResponse = await getData<any>(`${endpoints.getPregnancyHistory(benfDetails?.[0]?.benef_id)}`);
            setPregnancyHistory(pregnancyHistoryResponse.data || []);
            const familyPlanningDetailsResponse = await getData<any>(`${endpoints.getFamilyPlanningDetailsByBenfId}/${benfDetails?.[0]?.benef_id}`);
            dispatch(setFamilyPlanningDetails(familyPlanningDetailsResponse.data));
            let isLatestTerminalMethod;
            const spacingMethods = familyPlanningDetailsResponse.data?.filter((item: any) => item.contraception_category_code === "spacing_methods");
            const sortedSpacingMethods = spacingMethods?.sort(
              (a: any, b: any): any => {
                const bDate: any = new Date(b.service_date);
                const aDate: any = new Date(a.service_date);
                return bDate - aDate;
              });
            const terminalMethods = familyPlanningDetailsResponse.data?.filter((item: any) => item.contraception_category_code === "terminal_methods");
            const sortedTerminalMethods = terminalMethods?.sort(
              (a: any, b: any): any => {
                const bDate: any = new Date(b.service_date);
                const aDate: any = new Date(a.service_date);
                return bDate - aDate;
              });
            if (
              sortedSpacingMethods &&
              sortedSpacingMethods.length > 0 &&
              sortedTerminalMethods &&
              sortedTerminalMethods.length > 0
            ) {
              const spacingDate = new Date(sortedSpacingMethods[0].service_date);
              const terminalDate = new Date(sortedTerminalMethods[0].service_date);
              if (spacingDate > terminalDate) {
                 isLatestTerminalMethod = false;
              }else{
                isLatestTerminalMethod = true;
              }
            } else if( sortedTerminalMethods &&
                sortedTerminalMethods.length > 0){
                    isLatestTerminalMethod = true;
                }
        if(isLatestTerminalMethod && sortedTerminalMethods?.length>0){
          const familyPlanningDetailsByTransId = await getData<any>(`${endpoints.getFamilyPlanningDetailsById}/${sortedTerminalMethods[0].family_planning_transaction_id}`);
          const followupDetails:any = familyPlanningDetailsByTransId.data?.[0]?.followup_details;
          if(followupDetails?.length>0){
            let flag = false;
            followupDetails?.forEach((item:any)=>{
                if(item.followup_name === "Failure"){
                    setIsAddButtonDisable(false);
                    flag = true;
                    return;
                }
            })
            if(!flag){
                setIsAddButtonDisable(true);
            }
          }else{
            setIsAddButtonDisable(true);
          }
        }
        } catch(error:any){
            errorLogger(error?.response?.data||error?.response||error)
          }
    }

    const onClickAddServices = () => {
        const familyPlanningBackObj = {
            benfType: "female",
            menuItems: "Family Planning",
        }
        dispatch(setFamilyPlanningBackHandler(familyPlanningBackObj));
        navigate("/beneficiaries/family-planning-details",{state:{ flow:"Create",item:null}});
    }

    const handleFamilyPlanningServiceView = (item: any) => {
        const familyPlanningBackObj = {
            benfType: "female",
            menuItems: "Family Planning",
        }
        dispatch(setFamilyPlanningBackHandler(familyPlanningBackObj));
        navigate(`/beneficiaries/family-planning/overview`, {state: item});
    }

    const handleEditClick = (item:any) =>{
        const familyPlanningBackObj = {
            benfType: "female",
            menuItems: "Family Planning",
        }
        dispatch(setFamilyPlanningBackHandler(familyPlanningBackObj));
        navigate("/beneficiaries/family-planning-details",{state:{ flow:"Edit",item:item}});
    }

    const isServiceDateInPregnancyPeriod = (serviceDate: string): boolean => {
        return pregnancyHistory.some((pregnancy: any) => {
            const lmp = pregnancy.lmp_date;
            const closureDate = pregnancy.pregnancy_closure_date;
            if (!lmp) return false;
            const sDate = dayjs(serviceDate);
            if (closureDate) {
                return !sDate.isBefore(dayjs(lmp)) && sDate.isBefore(dayjs(closureDate));
            } else {
                return !sDate.isBefore(dayjs(lmp));
            }
        });
    };

    return (
        <>
            <div className='family-planning-services'>
                <div className='family-planning-header' >
                    <BadgesComponent
                        className="over-badge"
                        badgeName={"Family Planning Services"}
                        variant="primary-1"
                        badgeType="case5"
                    />
                </div>
                {userRole !== COMMON_CONSTANTS.FIELD_LEVEL_USER && (
                <Button
                    onClick={(!benfDetails?.[0]?.is_pregnant || !benfDetails?.[0]?.is_dead) ? onClickAddServices:()=>{}}
                    buttonName={buttonNames.addServicesButton}
                    disabled={benfDetails?.[0]?.is_pregnant || benfDetails?.[0]?.is_dead || isAddButtonDisable}
                    colorType={'primary'}
                    dataTestId={"add-services-btn"}
                    id={''}
                    icon={<PlusSvgIcon />}
                    iconPosition={'left'}
                />
            )}
            </div>
            <div style={{ width: '90%', marginTop: '20px' }}>
                {familyPlanningDetails?.map((item: any, index: any) => (
                    <div className="family-planning-registration-modal" key={index} >
                        <div className="family-planning-registration-header" >
                            <div className='family-planning-service-row-inner-left-container'>
                                <div>{item.contraception_method_name}</div>
                                <div className="family-planning-sub-services" >
                                    <div className="family-planning-service-tooltip">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24px" height="24px" fill='#A9328D'
                                        ><path d="M 12 2 C 6.4889971 2 2 6.4889971 2 12 C 2 17.511003 6.4889971 22 12 22 C 17.511003 22 22 17.511003 22 12 C 22 6.4889971 17.511003 2 12 2 z M 12 4 C 16.430123 4 20 7.5698774 20 12 C 20 16.430123 16.430123 20 12 20 C 7.5698774 20 4 16.430123 4 12 C 4 7.5698774 7.5698774 4 12 4 z M 11 7 L 11 9 L 13 9 L 13 7 L 11 7 z M 11 11 L 11 17 L 13 17 L 13 11 L 11 11 z"></path></svg>
                                        <div className="facility-tooltip">
                                            <div>
                                                <label>Facility Type</label>
                                                <p>{item?.facility_type_name ? item?.facility_type_name : '-'}</p>
                                            </div>
                                            <div>
                                                <label>Facility Name</label>
                                                <p>{item?.facility_name ? item?.facility_name : '-'}</p>
                                            </div>
                                            <div>
                                                <label>Health Provider Name</label>
                                                <p>{`${item?.healthprovider_f_name ? item?.healthprovider_f_name + ' ' + item?.healthprovider_l_name : '-'}`}</p>
                                            </div>
                                            <div>
                                                <label>Asha name</label>
                                                <p>{`${item?.mobiliser_f_name ? item?.mobiliser_f_name + ' ' + item?.mobiliser_l_name : '-'}`}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div style={{ width: '30%', textAlign: "center" }}>{dayjs(item.service_date).format('DD-MM-YYYY')}</div>
                            <div
                                className="family-planning-registration-Completed"
                                id="family-planning-registration-status"
                                style={{ width: '125px' }}
                            >
                                {latestFamilyPlanningCreateDate && dayjs(item.create_date).isSame(dayjs(latestFamilyPlanningCreateDate), 'day') && dayjs().diff(dayjs(latestFamilyPlanningCreateDate), 'day') <= 90 && !isServiceDateInPregnancyPeriod(item.service_date) && <span onClick={()=>handleEditClick(item)} style={{cursor:"pointer"}}><EditIcon /></span>}
                                <span data-testid="view-details-icon" onClick={() => handleFamilyPlanningServiceView(item)} style={{ cursor: 'pointer' }} >
                                    <EyeOpen />
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    )
}

export default FamilyPlanningServices;